import { ref, onMounted, onUnmounted, type Ref } from 'vue';
import { field, wireString } from '@/utils/wire';
import { useSocketStore } from '../stores/socket';

interface DeviceValueChangeEvent {
  deviceId: string;
  valueId: string;
  valueKey: string;
  value: unknown;
  timestamp: Date;
}

/**
 * Composable for subscribing to real-time device value updates via Socket.IO
 *
 * @param deviceId - Device ID (can be string or reactive ref)
 * @returns currentValues - Reactive record of valueKey to currentValue mappings
 *
 * Usage:
 * ```ts
 * const { currentValues } = useDeviceValues(props.device.deviceId);
 * const isOn = computed(() => currentValues.value['power'] === true);
 * ```
 */
export function useDeviceValues(deviceId: Ref<string> | string) {
  const socketStore = useSocketStore();
  const currentValues = ref<Record<string, unknown>>({});
  let lastProcessedEventSignature: string | null = null;

  function getEventSignature(data: DeviceValueChangeEvent): string | null {
    if (!data?.deviceId || !data.valueKey) return null;

    const timestamp =
      data.timestamp instanceof Date
        ? data.timestamp.toISOString()
        : data.timestamp !== undefined && data.timestamp !== null
          ? String(data.timestamp)
          : '';

    return [data.deviceId, data.valueId ?? '', data.valueKey, timestamp, JSON.stringify(data.value)].join(':');
  }

  function applyValueChanged(data: DeviceValueChangeEvent) {
    const id = typeof deviceId === 'string' ? deviceId : deviceId.value;

    if (data.deviceId !== id || !data.valueKey) {
      return;
    }

    const signature = getEventSignature(data);
    if (signature && signature === lastProcessedEventSignature) {
      return;
    }
    lastProcessedEventSignature = signature;

    if (!Object.is(currentValues.value[data.valueKey], data.value)) {
      currentValues.value[data.valueKey] = data.value;
    }
  }

  function handleValueChanged(data: DeviceValueChangeEvent) {
    applyValueChanged(data);
  }

  // Also listen to global device_value_changed as fallback
  function handleGlobalValueChanged(data: DeviceValueChangeEvent) {
    applyValueChanged(data);
  }

  let isSubscribed = false;

  onMounted(() => {
    const id = typeof deviceId === 'string' ? deviceId : deviceId.value;
    // console.log('[useDeviceValues] Subscribing to device:', id);
    // console.log('[useDeviceValues] Socket connected:', socketStore.connected);
    // console.log('[useDeviceValues] Socket instance:', socketStore.socket);

    // Only subscribe to the room once per device (multiple components might share the same device)
    socketStore.subscribe('device', id);

    // Register this component's specific handlers
    socketStore.on('value_changed', handleValueChanged);
    socketStore.on('device_value_changed', handleGlobalValueChanged);

    isSubscribed = true;
    // console.log('[useDeviceValues] Subscribed and listening for events');
  });

  onUnmounted(() => {
    if (!isSubscribed) return;

    const id = typeof deviceId === 'string' ? deviceId : deviceId.value;
    // console.log('[useDeviceValues] Unsubscribing from device:', id);

    // Remove only this component's specific handlers by passing the callback reference
    socketStore.off('value_changed', handleValueChanged);
    socketStore.off('device_value_changed', handleGlobalValueChanged);

    // Note: We don't unsubscribe from the room because other components might still be using it
    // The server will handle cleanup when all clients disconnect from the room
    socketStore.unsubscribe('device', id);

    isSubscribed = false;
  });

  /**
   * Initialize current values from a device values array
   * Call this in a watch or onMounted to sync initial state
   */
  function initializeValues(values: readonly unknown[], options?: { replace?: boolean }): void {
    const nextValues: Record<string, unknown> = options?.replace ? {} : currentValues.value;

    for (const row of values) {
      // Handle both camelCase (DeviceValue model) and snake_case (legacy/raw). The rows
      // arrive as untyped hub JSON, so they are read through the boundary helpers
      // (card t_be9a57e2): a non-string key is ignored by construction.
      const key = wireString(field(row, 'valueKey')) ?? wireString(field(row, 'value_key'));
      const camel = field(row, 'currentValue');
      const snake = field(row, 'current_value');
      const nextValue = camel !== undefined ? camel : snake !== undefined ? snake : null;

      if (key) {
        nextValues[key] = nextValue;
      }
    }

    if (options?.replace) {
      currentValues.value = nextValues;
    }
  }

  /**
   * Manually update a value (useful for optimistic updates if needed)
   */
  function updateValue(valueKey: string, value: unknown) {
    currentValues.value[valueKey] = value;
  }

  return {
    currentValues,
    initializeValues,
    updateValue,
  };
}
